import axios from 'axios';
import { API_URL } from '../config/api';

export interface EvalDatasetInfo {
  id: string;
  label: string;
  caseCount: number;
}

export interface EvalModels {
  production: string;
  models: string[];
}

export interface EvalRunSpec {
  sectorId: number;
  agentId: number;
  datasetId: string;
  epochs: number;
  model?: string;
  promptOverride?: string;
  concurrency?: number;
  rubricJudge?: boolean;
  pdfVisual?: boolean;
  simulator?: { enabled: boolean; model?: string; maxTurns?: number };
}

export interface EvalRunRow {
  id: number;
  sectorId: number;
  agentId: number;
  datasetId: string;
  status: string;
  model?: string | null;
  progressDone: number;
  progressTotal: number;
  summaryJson?: string | null;
  error?: string | null;
  cancelRequested: boolean;
  createdAt: string;
}

export interface EvalAggregate {
  mode: string;
  n: number;
  passed: number;
  transportErrors: number;
  passRate: number;
  meanScore: number;
  p10Score: number;
  p50Score: number;
  p90Score: number;
  meanLatencyMs: number;
  p50LatencyMs: number;
  p90LatencyMs: number;
}

export interface EvalArtifact {
  id: number;
  kind: string;
  byteSize: number;
  gateReason?: string | null;
}

export interface EvalEpochRow {
  id: number;
  caseId: string;
  epoch: number;
  mode: string;
  model?: string | null;
  pass: boolean;
  score: number;
  latencyMs: number;
  transferred: boolean;
  transportError?: string | null;
  pdfGateReason?: string | null;
  transcriptJson?: string | null;
  artifacts: EvalArtifact[];
}

export interface EvalRunDetail {
  run: EvalRunRow;
  epochs: EvalEpochRow[];
}

export interface EvalSummary {
  scripted: EvalAggregate;
  simulated: EvalAggregate;
  cases: { caseId: string; mode: string; stats: EvalAggregate }[];
}

const headers = (token: string) => ({
  Authorization: `Bearer ${token}`,
  'Content-Type': 'application/json',
});

export async function listEvalDatasets(token: string): Promise<EvalDatasetInfo[]> {
  const res = await axios.get(`${API_URL}/AiEval/datasets`, { headers: headers(token) });
  return res.data.data;
}

export async function listEvalModels(token: string): Promise<EvalModels> {
  const res = await axios.get(`${API_URL}/AiEval/models`, { headers: headers(token) });
  return res.data.data;
}

export async function createEvalRun(spec: EvalRunSpec, token: string): Promise<EvalRunRow> {
  const res = await axios.post(`${API_URL}/AiEval/runs`, spec, { headers: headers(token) });
  return res.data.data;
}

export async function listEvalRuns(sectorId: number, token: string): Promise<EvalRunRow[]> {
  const res = await axios.get(`${API_URL}/AiEval/runs`, {
    params: { sectorId },
    headers: headers(token),
  });
  return res.data.data;
}

export async function getEvalRun(id: number, token: string, includeTranscript = false): Promise<EvalRunDetail> {
  const res = await axios.get(`${API_URL}/AiEval/runs/${id}`, {
    params: { includeTranscript },
    headers: headers(token),
  });
  return res.data.data;
}

export async function cancelEvalRun(id: number, token: string): Promise<void> {
  await axios.post(`${API_URL}/AiEval/runs/${id}/cancel`, {}, { headers: headers(token) });
}

export async function deleteEvalRun(id: number, token: string): Promise<void> {
  await axios.delete(`${API_URL}/AiEval/runs/${id}`, { headers: headers(token) });
}

export async function compareEvalRuns(id: number, otherId: number, token: string): Promise<{
  left: { run: EvalRunRow; summary: EvalSummary };
  right: { run: EvalRunRow; summary: EvalSummary };
}> {
  const res = await axios.get(`${API_URL}/AiEval/runs/${id}/compare/${otherId}`, { headers: headers(token) });
  return res.data.data;
}

export async function fetchEvalArtifact(id: number, token: string): Promise<Blob> {
  const res = await axios.get(`${API_URL}/AiEval/artifacts/${id}`, {
    headers: { Authorization: `Bearer ${token}` },
    responseType: 'blob',
  });
  return res.data;
}

export function parseSummary(json?: string | null): EvalSummary | null {
  if (!json) return null;
  try {
    return JSON.parse(json) as EvalSummary;
  } catch {
    return null;
  }
}
